<template>
    <div class="flex flex-col">
        <div class="grid grid-cols-5">
            <p class="col-span-1 text-heading-1 font-semibold">{{ titleByStep[step] }}</p>
            <Steps
                v-if="form.editable"
                v-model="step"
                class="col-span-3 mx-auto"
                :steps="tabs"
                with-chevron
            />
            <div class="flex justify-end" :class="{ 'col-span-1': form.editable, 'col-span-4': !form.editable }">
                <simple-button
                    look="primary"
                    icon="keyboard-arrow-left"
                    icon-position="left"
                    @click="$emit('close')"
                >
                    {{ t('multi-posting.back-to-campaigns') }}
                </simple-button>
            </div>
        </div>
        <div class="border-t w-full my-24"></div>
        <component
            :is="componentByStep[`step${step}`]"
            @next="nextStep"
            @previous="previousStep"
            @saveProducts="saveProducts"
            :direction="direction">
            <template #basket>
                <Basket
                    :step="step"
                    @next="nextStep"
                    @previous="previousStep"
                    @purchase="purchase"
                    @saveProducts="saveProducts"
                    @saveDetails="saveDetails" />
            </template>
        </component>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import {computed, ref, onBeforeMount, onBeforeUnmount, watch} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import CampaignFormChannels from './CampaignFormChannels.vue';
import CampaignFormDetails from './CampaignFormDetails.vue';
import CampaignFormPostingRequirements from './CampaignFormPostingRequirements.vue';
import CampaignFormReview from './CampaignFormReview.vue';
import Basket from './form/Basket.vue';
import * as Vonq from '/../../vendor/jobtoolz/vonq/public/js/share/Vonq.js';
import { useMainStore } from '../../state/store';
import { useMultiPostingStore } from '../../state/modules/multiPosting';
import { useNotificationsStore } from '../../state/modules/notifications';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Steps from "../Steps.vue";
import PaidSubscription from "../../models/PaidSubscription";
import SimpleButton from "../ui/SimpleButton.vue";
import { useRepo } from 'pinia-orm';


const mainStore = useMainStore();
const multiPostingStore = useMultiPostingStore();
const notificationStore = useNotificationsStore();
const PaidSubscriptionRepo = useRepo(PaidSubscription)
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const step = ref(1);
const direction = ref('');
const campaignId = ref(route.query.campaignId || '');

const form = computed(() => multiPostingStore.form);
const getLookupEntryByKey = multiPostingStore.getLookupEntryByKey;
const selectedItems = computed(() => multiPostingStore.selectedItems);
const defaultStep = computed(() => multiPostingStore.defaultStep);
const currentCompany = computed(() => mainStore.currentCompany);
const tabs = computed(() => [
    {
        title: t('multi-posting.form.tabs.channels'),
    },
    {
        title: t('multi-posting.form.tabs.details'),
        disabled: step.value < 2
    },
    {
        title: t('multi-posting.form.tabs.channel_details'),
        disabled: step.value < 3,
        hidden: hideContractsTab(),
    },
    {
        title: t('multi-posting.form.tabs.review'),
        disabled: step.value < 4
    }
]);

const componentByStep = computed(() => ({
    step1: CampaignFormChannels,
    step2: CampaignFormDetails,
    step3: CampaignFormPostingRequirements,
    step4: CampaignFormReview,
}));

const titleByStep = computed(() => ({
    1: t('multi-posting.channels.title'),
    2: t('multi-posting.campaigns.form.posting_details.title'),
    3: t('multi-posting.tabs.contracts.label'),
    4: t('general.review'),
}));

const selectedContracts = computed(() => selectedItems.value.filter((item) => item.type === 'contract'));

const purchase = async () => {
    try {
        return multiPostingStore.purchaseCampaign(campaignId.value)
            .then((results) => {
                window.bus.dispatch('close-campaign-form');
                notificationStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: t('multi-posting.campaigns.form.purchase_campaign.success', { vacancy: results.name }),
                });
            });
    } catch (error) {
        notificationStore[ADD_NOTIFICATION]({
            type: 'error',
            message: error,
        });
    }
};

const nextStep = () => {
    direction.value = 'forwards';
    step.value++;
};

const previousStep = () => {
    direction.value = 'backwards';
    step.value--;
};

const campaignData = () => {
    let data = {
        products: [],
        details: {},
    };

    return {
        get() {
            return data;
        },
        addProducts(p) {
            for (const product of p) {
                data.products.push({
                    productId: product.id,
                    contractId: product.contract_id ?? null,
                    productName: product.name,
                    duration: product.duration.period,
                    price: product.price.amount,
                    currency: product.price.currency,
                    channelId: product.channel_id,
                    type: product.type,
                    timeToProcess: product.time_to_process,
                    timeToSetup: product.time_to_setup,
                });
            }
            return this;
        },
        addDetails(d, lookupFn) {
            const jobTitle = lookupFn('jobTitles', d.jobTitleId);
            const education = lookupFn('educationLevels', d.educationLevelId);
            const seniority = lookupFn('seniority', d.seniorityId);
            const industry = lookupFn('industries', d.industryId);
            const jobFunction = lookupFn('jobFunctions', d.jobFunctionId);
            data.details = {
                title: d.title,
                description: d.description,
                startDate: d.startDate,
                publicationLanguage: d.publicationLanguage,
                country: d.region,
                jobId: d.jobId,
                jobTitleId: d.jobTitleId,
                jobTitle: jobTitle ? jobTitle.label : '',
                educationId: d.educationLevelId,
                educationName: education ? education.label : '',
                seniorityId: d.seniorityId,
                seniorityName: seniority ? seniority.label : '',
                industryId: d.industryId,
                industryName: industry ? industry.label : '',
                jobFunctionId: d.jobFunctionId,
                jobFunctionName: jobFunction ? jobFunction.label : '',
                price: d.price,
                yearsOfExperience: d.yearsOfExperience,
                employmentType: d.employmentType,
                weeklyWorkingHoursFrom: d.weeklyWorkingHoursFrom,
                weeklyWorkingHoursTo: d.weeklyWorkingHoursTo,
                salaryIndicationPeriod: d.salaryIndicationPeriod,
                salaryIndicationFrom: d.salaryIndicationFrom,
                salaryIndicationTo: d.salaryIndicationTo,
                salaryIndicationCurrency: d.salaryIndicationCurrency,
                extra: {
                    user_id: d.userId,
                    requested_by: d.requestedBy,
                    duration: d.duration,
                    post_link: d.postLink,
                    vacancy_name: d.vacancyName,
                },
            };
            return this;
        },
    };
};

const saveCampaign = async (data, success) => {
    try {
        return Vonq.api({
            action: 'saveCampaign',
            request: data,
            userId: currentCompany.value.id,
            campaignId: campaignId.value,
        }).then(({ guid }) => {
            form.value.resetErrors();
            addCampaignToRequest(guid);
            success();
        })
    } catch (error) {
        if (error.response?.data) {
            form.value._handleError(error);
            if (error.response.data.error) {
                notificationStore[ADD_NOTIFICATION]({
                    type: 'error',
                    message: error.response.data.error,
                });
            }
        }
        throw error;
    }
};

const saveProducts = ({ p, inRemoval = false }) => {
    const data = campaignData().addProducts(p).get();
    saveCampaign(data, () => {
        if (!inRemoval) {
            nextStep();
        }
    });
};

const saveDetails = (d) => {
    const data = campaignData()
        .addProducts(selectedItems.value)
        .addDetails(d, getLookupEntryByKey)
        .get();
    saveCampaign(data, () => {
        nextStep();
    });
};

const addCampaignToRequest = (newCampaignId) => {
    if (newCampaignId !== campaignId.value) {
        campaignId.value = newCampaignId;
        router.push({ query: { ...route.query, campaignId: newCampaignId } });
    }
};

const hideContractsTab = () => {
    let hasMultiposting = !!PaidSubscriptionRepo
        .where('type', 'multi-posting')
        .where('company_id', currentCompany.value.id)
        .where('active', true)
        .first() && currentCompany.value.multiposting_status === 'active';
    let hasContracts = selectedContracts.value.length > 0;
    return !hasContracts || !hasMultiposting;
}

watch(
    () => step.value,
    (newStep, oldStep) => {
        if (newStep === 3 && selectedContracts.value.length === 0) {
            newStep > oldStep ? nextStep() : previousStep();
        }
    }
);

onBeforeMount(() => {
    step.value = defaultStep.value;
});

onBeforeUnmount(() => {
    multiPostingStore.clearCreatingCampaign();
});
</script>
